<template>
  <li class="col-span-1">
    <div
      class="group w-full h-full flex flex-col bg-white rounded-xl border border-untitled-gray-200 shadow-sm hover:shadow-md hover:border-untitled-gray-300 transition-all duration-200 overflow-hidden"
      :id="`my_video_card_${video.id}`"
    >
      <!-- Thumbnail -->
      <div
        class="relative w-full aspect-video cursor-pointer overflow-hidden bg-untitled-gray-100"
        @click="handleClickOpen"
      >
        <img
          v-lazy="{
            src: video.thumbnail,
            error:
              'https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg',
            loading:
              'https://via.placeholder.com/800x450?text=Thumbnail%20Loading...',
          }"
          class="object-cover object-center w-full h-full transition-transform duration-300 group-hover:scale-105"
        />
        <div
          class="absolute inset-0 flex justify-center items-center bg-untitled-gray-900/0 group-hover:bg-untitled-gray-900/30 transition-colors duration-200"
        >
          <component
            :is="overlayIcon"
            class="w-12 h-12 text-white transition-opacity duration-200"
            :class="
              isCompleted ? 'opacity-80' : 'opacity-0 group-hover:opacity-90'
            "
          />
        </div>

        <!-- Duration -->
        <div
          v-if="durationLabel"
          class="absolute bottom-2 right-2 bg-untitled-gray-900/80 text-white text-[11px] font-semibold px-2 py-0.5 rounded"
        >
          {{ durationLabel }}
        </div>

        <!-- Top-left badge -->
        <div
          v-if="isDraft"
          class="absolute top-2 left-2 bg-amber-600/90 text-white text-[11px] font-semibold uppercase px-2.5 py-1 rounded-full"
        >
          Draft
        </div>
        <div
          v-else
          class="absolute top-2 left-2 flex items-center gap-1 bg-untitled-gray-800/90 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full"
        >
          <component :is="typeIcon" class="w-3.5 h-3.5" />
          {{ typeLabel }}
        </div>

        <!-- Top-right badge -->
        <div
          v-if="isCompleted"
          class="absolute top-2 right-2 flex items-center gap-1 font-semibold text-green-700 text-[11px] bg-green-100 px-2.5 py-1 rounded-full"
        >
          Completed
        </div>
        <div
          v-else-if="isShared"
          class="absolute top-2 right-2 flex items-center gap-1 font-semibold text-blue-700 text-[11px] bg-blue-100 px-2.5 py-1 rounded-full"
        >
          <UsersIcon class="w-3.5 h-3.5" />
          Shared
        </div>
      </div>

      <!-- Body -->
      <div class="flex flex-col gap-3 p-4 flex-1">
        <div class="w-full flex flex-row justify-start items-center">
          <el-tooltip
            :content="wrapTooltipContent(video.name)"
            effect="dark"
            :raw-content="true"
            placement="top-start"
          >
            <h1
              class="text-base font-semibold min-w-0 flex-1 truncate text-untitled-gray-800 line-clamp-1 cursor-pointer"
              @click="handleClickOpen"
            >
              {{ video.name }}
            </h1>
          </el-tooltip>
          <ElDropdown trigger="click">
            <ElButton size="small" text>
              <DotsVerticalIcon class="w-5 h-5" />
            </ElButton>
            <template #dropdown>
              <ElDropdownMenu>
                <ElDropdownItem @click="handleClickEdit">
                  <PencilAltIcon class="w-4 h-4 mr-2" />
                  Edit
                </ElDropdownItem>
                <ElDropdownItem
                  @click="handleClickDelete"
                  :disabled="loadingDelete"
                  divided
                  class="!text-red-600"
                >
                  <TrashIcon class="w-4 h-4 mr-2" />
                  Delete
                </ElDropdownItem>
              </ElDropdownMenu>
            </template>
          </ElDropdown>
        </div>

        <!-- Meta -->
        <div
          class="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-untitled-gray-500"
        >
          <div v-if="courseName" class="flex items-center gap-1 min-w-0">
            <FolderIcon class="w-4 h-4 text-untitled-gray-400 shrink-0" />
            <span class="font-medium truncate max-w-[10rem]">{{
              courseName
            }}</span>
          </div>
          <div class="flex items-center gap-1">
            <CalendarIcon class="w-4 h-4 text-untitled-gray-400" />
            {{ formatDate(video.created_at, "MM/DD/YYYY") }}
          </div>
        </div>

        <!-- Tags -->
        <div
          v-if="visibleTags.length"
          class="flex flex-wrap items-center gap-1.5"
        >
          <span
            v-for="tag in visibleTags"
            :key="tag"
            class="inline-flex items-center gap-1 truncate text-[11px] font-medium text-untitled-gray-600 bg-untitled-gray-100 px-2 py-0.5 rounded-full"
          >
            <TagIcon class="w-3 h-3" />
            {{ tag.name }}
          </span>
          <span
            v-if="hiddenTagsCount > 0"
            class="text-[11px] font-medium text-untitled-gray-400"
          >
            +{{ hiddenTagsCount }} more
          </span>
        </div>

        <!-- Actions -->
        <div class="mt-auto pt-1 flex flex-row flex-wrap gap-2">
          <div v-if="isOwner" class="flex w-full items-center gap-2 mx-auto">
            <AssignToUser
              v-if="isOwner"
              :unit-id="unitId"
              isUnit
              v-slot="{ clickAssign }"
              title="Assign Video"
              label="Assign"
            >
              <button
                type="button"
                @click="clickAssign"
                v-has-restrict-learner
                class="inline-flex flex-1 justify-center items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-untitled-gray-700 bg-untitled-gray-100 hover:bg-untitled-gray-200 transition-colors"
                title="Assign Video"
              >
                <LinkIcon class="w-4 h-4" />
                Assign
              </button>
            </AssignToUser>
          </div>

          <button
            type="button"
            @click="handleClickOpen"
            class="flex-1 min-w-full inline-flex justify-center items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-white bg-app-primary hover:bg-app-secondary transition-colors"
          >
            <CursorClickIcon class="w-4 h-4" />
            <span class="truncate">{{ primaryLabel }}</span>
          </button>
        </div>
      </div>
    </div>
  </li>
</template>

<script setup>
import { computed, defineProps } from "vue";
import {
  dayjs,
  ElTooltip,
  ElMessageBox,
  ElMessage,
  ElDropdown,
  ElButton,
  ElDropdownMenu,
  ElDropdownItem,
} from "element-plus";
import { wrapTooltipContent } from "@/composable/wrapTooltipContent";
import {
  CalendarIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  PlayIcon,
  VideoCameraIcon,
  TagIcon,
  PencilAltIcon,
  TrashIcon,
  CursorClickIcon,
  FolderIcon,
  UsersIcon,
  LinkIcon,
} from "@heroicons/vue/solid";
import AssignToUser from "@/components/utilities/AssignToUser.vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores";
import { storeToRefs } from "pinia";
import { useMyVideosStore } from "@/stores/my-videos/my-videos.store";
import { DotsVerticalIcon } from "@heroicons/vue/outline";

const props = defineProps({
  video: {
    type: Object,
    required: true,
  },
});

const router = useRouter();
const authStore = useAuthStore();
const myVideosStore = useMyVideosStore();
const { loadingDelete } = storeToRefs(myVideosStore);

const video = computed(() => props.video);
const unitId = computed(() => video.value.unit_id ?? video.value.id);
const isCompleted = computed(() => Boolean(video.value.is_completed));
const isDraft = computed(() => Boolean(video.value.is_draft));
const isShared = computed(() => Boolean(video.value.is_shared));
const isTextType = computed(() => video.value.type === "text");
const courseName = computed(() => video.value.module?.course?.name ?? "");
const isOwner = computed(
  () => video.value.created_by === authStore.user?.id || authStore.isSuperAdmin
);

const visibleTags = computed(() => (video.value.tags ?? []).slice(0, 2));
const hiddenTagsCount = computed(
  () => (video.value.tags?.length ?? 0) - visibleTags.value.length
);

const typeLabel = computed(() => (isTextType.value ? "Text Unit" : "Video"));
const typeIcon = computed(() =>
  isTextType.value ? DocumentTextIcon : VideoCameraIcon
);

const overlayIcon = computed(() => {
  if (isCompleted.value) return CheckCircleIcon;
  return isTextType.value ? DocumentTextIcon : PlayIcon;
});

const durationLabel = computed(() => {
  if (isTextType.value) return "";
  if (!video.value.video_duration || video.value.video_duration === "00:00")
    return "";
  return video.value.video_duration;
});

const primaryLabel = computed(() => {
  const watched =
    video.value.is_video_played || video.value.is_finished_watching;

  if (isCompleted.value) {
    return isTextType.value ? "Review Content" : "Rewatch Video";
  }

  if (watched) {
    return isTextType.value ? "Continue Reading" : "Continue Watching";
  }

  return isTextType.value ? "Start Reading" : "Start Watching";
});

const formatDate = (value, format) => {
  return value ? dayjs(value).format(format) : "";
};

const handleClickOpen = () => {
  router.push({ name: "units", params: { id: unitId.value } });
};

const handleClickEdit = () => {
  router.push(`/edit/unit/${video.value.id}`);
};

const handleClickDelete = () => {
  ElMessageBox.confirm(
    `Are you sure you want to delete "${video.value.name}"? This cannot be undone.`,
    "Delete Video",
    {
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonClass: "!bg-red-600 !border-red-600 hover:!bg-red-700",
      type: "warning",
    }
  )
    .then(async () => {
      await myVideosStore.handleDeleteMyVideo(video.value.id);
      ElMessage.success("Video deleted successfully.");
    })
    .catch(() => {});
};
</script>

<style lang="css" scoped></style>
